/**
 * My Services Page
 * Shows helper's confirmed and completed services
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { requestsAPI, helpersAPI } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  MapPin,
  Calendar,
  Clock,
  Ship,
  MessageCircle,
  CheckCircle,
  Play,
  Filter,
  Star,
  Trash2,
  RefreshCw,
  X,
  Anchor,
  Briefcase,
} from 'lucide-react';
import { format } from 'date-fns';

function MyServices() {
  const { showToast } = useNotifications();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      // Use dedicated endpoint that returns all services including completed
      const data = await helpersAPI.getMyServices();
      setServices(data.data || []);
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setLoading(false);
    }
  };

  // Use request_id when available (from service-based queries), fallback to id
  const getRequestId = (service) => service.request_id || service.id;

  const handleStartService = async (service) => {
    const reqId = getRequestId(service);
    setActionLoading(service.id);
    try {
      await requestsAPI.startService(reqId);
      showToast('Service started!', 'success');
      loadServices();
    } catch (error) {
      showToast(error.message || 'Failed to start service', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCompleteService = async (service) => {
    const reqId = getRequestId(service);
    setActionLoading(service.id);
    try {
      await requestsAPI.completeService(reqId);
      showToast('Service marked as complete! Waiting for owner confirmation.', 'success');
      loadServices();
    } catch (error) {
      showToast(error.message || 'Failed to complete service', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleWithdraw = async (service) => {
    if (!window.confirm('Are you sure you want to withdraw from this job?')) return;
    const reqId = getRequestId(service);
    setActionLoading(service.id);
    try {
      await requestsAPI.withdrawApplication(reqId);
      showToast('Withdrawn from job', 'success');
      loadServices();
    } catch (error) {
      console.error('Withdraw failed:', error);
      // If withdraw fails, try dismiss as fallback
      try {
        await requestsAPI.dismissRequest(reqId);
        showToast('Removed from your services', 'success');
        loadServices();
      } catch (dismissError) {
        showToast(error.message || 'Failed to withdraw from job', 'error');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleHideService = async () => {
    if (!serviceToDelete) return;
    const reqId = getRequestId(serviceToDelete);
    setActionLoading(serviceToDelete.id);
    try {
      await helpersAPI.hideService(reqId);
      showToast('Service removed from list', 'success');
      setServices(prev => prev.filter(s => s.id !== serviceToDelete.id));
    } catch (error) {
      showToast(error.message || 'Failed to hide service', 'error');
    } finally {
      setActionLoading(null);
      setShowDeleteConfirm(false);
      setServiceToDelete(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      matched: 'bg-green-100 text-green-800',
      in_progress: 'bg-green-100 text-green-800', // Treat as active
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      matched: 'Active',
      in_progress: 'Active', // Treat as active
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  };

  const filterTabs = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'completed', label: 'Completed' },
  ];

  const filteredServices = services.filter(service => {
    if (filter === 'all') return true;
    if (filter === 'active') return service.status === 'matched' || service.status === 'in_progress';
    return service.status === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-navy-900">
          My Services
        </h1>
        <p className="text-navy-600 mt-1">
          Track your upcoming and completed services
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
        <Filter className="h-4 w-4 text-navy-400 flex-shrink-0" />
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filter === tab.key
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-navy-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Services List */}
      {filteredServices.length === 0 ? (
        <div className="card text-center py-12">
          <Ship className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-navy-900 mb-2">
            No services found
          </h3>
          <p className="text-navy-600 mb-6">
            {filter === 'all'
              ? "You haven't been selected for any services yet"
              : `No ${filter.replace('_', ' ')} services`}
          </p>
          <Link to="/helper/requests" className="btn-primary">
            Browse Available Requests
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredServices.map((service) => (
            <div key={service.id} className="card">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    {/* Service Type Badge */}
                    {service.request_category === 'position' ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                        <Briefcase className="h-3 w-3 mr-1" />
                        Crew
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-800">
                        <Anchor className="h-3 w-3 mr-1" />
                        Mooring
                      </span>
                    )}
                    <MapPin className="h-5 w-5 text-primary-600" />
                    <h3 className="font-medium text-navy-900 text-lg">
                      {service.port_name}
                    </h3>
                    <span className={`badge ${getStatusBadge(service.status)}`}>
                      {getStatusLabel(service.status)}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-4 text-sm text-navy-600">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {format(new Date(service.scheduled_date), 'EEEE, MMM d, yyyy')}
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {service.scheduled_time}
                    </div>
                    <div className="flex items-center">
                      <Ship className="h-4 w-4 mr-1" />
                      {service.yacht_name} ({service.yacht_length}m)
                    </div>
                  </div>

                  {service.owner_name && (
                    <p className="mt-2 text-sm text-navy-500">
                      Owner: {service.owner_name}
                    </p>
                  )}

                  {service.notes && (
                    <p className="mt-2 text-sm text-navy-600 bg-gray-50 p-2 rounded">
                      {service.notes}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-4 lg:mt-0 lg:ml-4 flex flex-wrap gap-2">
                  {(service.status === 'matched' || service.status === 'confirmed') && (
                    <>
                      <Link
                        to={`/helper/requests/${getRequestId(service)}`}
                        className="btn-secondary py-2 px-3"
                      >
                        <MessageCircle className="h-4 w-4 mr-1" />
                        View & Chat
                      </Link>
                      <button
                        onClick={() => handleCompleteService(service)}
                        disabled={actionLoading === service.id}
                        className="btn-primary py-2"
                      >
                        {actionLoading === service.id ? (
                          <span className="spinner h-4 w-4" />
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Mark Complete
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleWithdraw(service)}
                        disabled={actionLoading === service.id}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        title="Withdraw from job"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}

                  {service.status === 'in_progress' && (
                    <>
                      <Link
                        to={`/helper/requests/${getRequestId(service)}`}
                        className="btn-secondary py-2 px-3"
                      >
                        <MessageCircle className="h-4 w-4 mr-1" />
                        View & Chat
                      </Link>
                      <button
                        onClick={() => handleCompleteService(service)}
                        disabled={actionLoading === service.id}
                        className="btn-primary py-2"
                      >
                        {actionLoading === service.id ? (
                          <span className="spinner h-4 w-4" />
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Mark Complete
                          </>
                        )}
                      </button>
                    </>
                  )}

                  {(service.status === 'completed' || service.status === 'cancelled') && (
                    <div className="flex items-center gap-2">
                      {service.status === 'completed' && service.rating ? (
                        <span className="flex items-center text-navy-500">
                          <Star className="h-4 w-4 text-gold-500 mr-1" />
                          {service.rating.toFixed(1)} rating
                        </span>
                      ) : service.status === 'completed' ? (
                        <span className="text-sm text-navy-500">Awaiting rating</span>
                      ) : null}
                      <button
                        onClick={() => {
                          setServiceToDelete(service);
                          setShowDeleteConfirm(true);
                        }}
                        disabled={actionLoading === service.id}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                        title="Remove from list"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hide Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-navy-900 mb-2">
                Remove this service from your list?
              </h3>
              <p className="text-sm text-navy-600 mb-6">
                This will hide the service from your list. This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setServiceToDelete(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleHideService}
                  disabled={actionLoading === serviceToDelete?.id}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {actionLoading === serviceToDelete?.id ? (
                    <span className="flex items-center">
                      <span className="spinner h-4 w-4 mr-2" />
                      Removing...
                    </span>
                  ) : (
                    'Remove'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyServices;
